import { z } from "zod";
import { initContract } from "@ts-rest/core";
import { StreamableFile } from "@nestjs/common";
import {
    clientsQuerySchema,
    clientsResponseSchema,
    getWorkspacesQuerySchema,
} from "@common/schemas/client.schema";
import {
    clientsCloseOverviewQuerySchema,
    clientsCloseOverviewResponseSchema,
    clientsCloseOverviewSlimQuerySchema,
    clientsCloseOverviewSlimResponseSchema,
    clientsCloseOverviewDetailsQuerySchema,
    clientsCloseOverviewDetailsResponseSchema,
    closeUnreadTaskCountsQuerySchema,
    closeUnreadTaskCountsResponseSchema,
} from "@common/schemas/clientsCloseOverview.schema";
import {
    workspacesListQuerySchema,
    workspacesListResponseSchema,
    workspaceContextResponseSchema,
    updateWorkspacePreferencesBodySchema,
} from "@common/schemas/workspace.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    workspaceDetailsResponseSchema,
    workspacesDetailsPageResponseSchema,
} from "@common/schemas/domains/workspace/workspace-details-response.schema";

const c = initContract();

/**
 * Workspace-scoped routes (by UUID) live under `/workspace/:workspaceId` so they
 * cannot shadow org/workspace list endpoints (`/clients`, `/clients-close-overview`, etc.).
 */
export const workspacesContract = c.router(
    {
        getWorkspacesList: {
            method: "GET",
            path: "/list",
            query: workspacesListQuerySchema,
            responses: {
                200: workspacesListResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },

        getWorkspaces: {
            method: "GET",
            path: "/",
            query: getWorkspacesQuerySchema,
            responses: {
                200: workspacesDetailsPageResponseSchema,
            },
        },
        getClients: {
            method: "GET",
            path: "/clients",
            query: clientsQuerySchema,
            responses: {
                200: clientsResponseSchema,
                400: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        exportClientsCsv: {
            method: "GET",
            path: "/clients/export",
            query: clientsQuerySchema,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getClientsCloseOverview: {
            method: "GET",
            path: "/clients-close-overview",
            query: clientsCloseOverviewQuerySchema,
            responses: {
                200: clientsCloseOverviewResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getClientsCloseOverviewSlim: {
            method: "GET",
            path: "/clients-close-overview/slim",
            query: clientsCloseOverviewSlimQuerySchema,
            responses: {
                200: clientsCloseOverviewSlimResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getClientsCloseOverviewDetails: {
            method: "GET",
            path: "/clients-close-overview/details",
            query: clientsCloseOverviewDetailsQuerySchema,
            responses: {
                200: clientsCloseOverviewDetailsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getCloseUnreadCounts: {
            method: "GET",
            path: "/clients-close-overview/unread-counts",
            query: closeUnreadTaskCountsQuerySchema,
            responses: {
                200: closeUnreadTaskCountsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getWorkspaceDetails: {
            method: "GET",
            path: "/workspace/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: workspaceDetailsResponseSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getWorkspaceContext: {
            method: "GET",
            path: "/workspace/:workspaceId/context",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: workspaceContextResponseSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        updateWorkspacePreferences: {
            method: "PATCH",
            path: "/workspace/:workspaceId/preferences",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: updateWorkspacePreferencesBodySchema,
            responses: {
                200: workspaceDetailsResponseSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/workspaces",
    },
);
